// auth/layouts/auth/AuthContext.jsx
import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import api, { apiAuth } from "services/api";

const AuthContext = createContext(undefined);

// Opcional: si quieres persistir el id del impersonado tras recargar la página, usa esta key
const DEV_IMPERSONATED_USER_ID = "dev_impersonated_user_id";

function getBaseURL() {
  try {
    const ls = localStorage.getItem("api_base_url");
    if (ls) return ls;
  } catch {}
  // eslint-disable-next-line no-undef
  const apiBase =
  typeof process !== "undefined"
    ? process.env?.REACT_APP_API_BASE_URL
    : "";
  const cra  = typeof process !== "undefined" && process?.env?.REACT_APP_API_BASE_URL;
  return apiBase || cra || "";
}

function unwrapMe(data) {
  if (data?.user) return data.user;
  if (data?.data?.user) return data.data.user;
  return data?.data ?? data ?? null;
}

const isAdmin = (u) =>
  u?.is_admin === true || u?.is_admin === 1 || String(u?.is_admin) === "1";

function normalizeUserGroups(u) {
  if (!u) return u;

  const groups = Array.isArray(u.groups)
    ? u.groups
        .map((g) =>
          typeof g === "object" && g !== null
            ? g.id ?? g._id
            : g
        )
        .filter((id) => id !== null && id !== undefined && id !== "")
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id))
    : u.group_id !== null &&
      u.group_id !== undefined &&
      u.group_id !== ""
    ? [Number(u.group_id)].filter((id) => Number.isFinite(id))
    : [];

  return {
    ...u,
    groups,
  };
}

export function AuthProvider({ children }) {
  // Usuario real (admin u otro) devuelto por /auth/me
  const [baseUser, setBaseUser] = useState(null);
  // Usuario “representado” (si hay impersonación)
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    } catch { return ""; }
  });

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    const base = getBaseURL();
    if (base) {
      api.defaults.baseURL = base;
    }
  }, []);

  // Mantén Authorization en api cuando cambie token
  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, [token]);

  // Traer perfil con el token (si /auth/me existe) — SOLO actualiza baseUser
  const refreshMe = useCallback(
    async (overrideToken) => {
      const prev = api.defaults.headers.common.Authorization;
      if (overrideToken) api.defaults.headers.common.Authorization = `Bearer ${overrideToken}`;
      try {
        const candidates = ["/auth/me", "/user/me", "/me"];
        for (const url of candidates) {
          try {
            const { data } = await api.get(url);
            const u = unwrapMe(data);
            if (u) {
              const normalized = normalizeUserGroups(u);
              setBaseUser(normalized);
              return normalized;
            }
          } catch {}
        }
        throw new Error("No se pudo obtener el perfil.");
      } finally {
        if (overrideToken) api.defaults.headers.common.Authorization = prev;
      }
    },
    []
  );

  // Si ya había token, intenta perfil (no bloqueante)
  useEffect(() => { if (token) refreshMe().catch(()=>{}); }, [token, refreshMe]);

  // Carga usuario representado tras recargar (opcional)
  useEffect(() => {
    (async () => {
      const id = sessionStorage.getItem(DEV_IMPERSONATED_USER_ID);
      if (id && token) {
        try {
          const { data } = await api.get(`/user/${id}`);
          const u = data?.user ?? data?.data ?? data ?? null;
          if (u) {
            setImpersonatedUser(normalizeUserGroups(u));
          } else {
            sessionStorage.removeItem(DEV_IMPERSONATED_USER_ID);
          }
        } catch {
          sessionStorage.removeItem(DEV_IMPERSONATED_USER_ID);
        }
      }
    })();
  }, [token]);

  // ---------- IMPERSONACIÓN ----------
  const devImpersonateAs = useCallback(async (targetUserId) => {
    if (!isAdmin(baseUser)) {
      throw new Error("Solo un administrador puede impersonar.");
    }
    // Trae usuario objetivo con el token del admin
    const { data } = await api.get(`/user/${targetUserId}`);
    const target = data?.user ?? data?.data ?? data ?? null;
      if (!target) throw new Error("No se pudo cargar el usuario objetivo.");

      const normalizedTarget = normalizeUserGroups(target);

      setImpersonatedUser(normalizedTarget);

      try {
        sessionStorage.setItem(
          DEV_IMPERSONATED_USER_ID,
          String(targetUserId)
        );
      } catch {}

      return normalizedTarget;
  }, [baseUser]);

  const stopImpersonating = useCallback(async () => {
    setImpersonatedUser(null);
    sessionStorage.removeItem(DEV_IMPERSONATED_USER_ID);
    // Opcionalmente refresca baseUser para asegurarte de que es el admin actual
    try { 
      const u = await refreshMe();
    } catch {}
  }, [refreshMe]);

  // ---------- LOGIN ----------
  const login = useCallback(async (email, password, remember = true) => {
    sessionStorage.removeItem(DEV_IMPERSONATED_USER_ID);
    setImpersonatedUser(null);

    setLoading(true);
    setError(null);

    try {
      const endpoint =
        localStorage.getItem("api_login_path") || "/auth/login";

      const { data } = await apiAuth.post(endpoint, {
        email,
        password,
      });

      const tk =
        data.token ||
        data.access_token ||
        data.jwt;

      if (!tk) {
        throw new Error(
          "La API respondió pero no incluyó token."
        );
      }

      setToken(tk);

      (remember
        ? localStorage
        : sessionStorage
      ).setItem("token", tk);

      let resolvedUser;

      try {
        resolvedUser = await refreshMe(tk);
      } catch {
        resolvedUser = normalizeUserGroups(
          data.user || { email }
        );

        setBaseUser(resolvedUser);
      }

      return resolvedUser;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e.message ||
        "Error al iniciar sesión";

      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [refreshMe]);

  const logout = useCallback(() => {
    setImpersonatedUser(null);
    setBaseUser(null);
    setToken("");
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem(DEV_IMPERSONATED_USER_ID);
    delete api.defaults.headers.common.Authorization;
  }, []);

  // Lo que ve el resto de la app:
  const user = impersonatedUser || baseUser;
  const isImpersonating = !!impersonatedUser;

  const value = useMemo(() => ({
    // Identidad efectiva (UI/Permisos deben leer esta)
    user,
    // Extra: si en alguna vista necesitas el admin real
    baseUser,
    impersonatedUser,

    token,
    isAuth: !!token,

    login,
    logout,
    refreshMe,
    loading,
    error,

    isImpersonating,
    devImpersonateAs,
    stopImpersonating,
    endImpersonation: stopImpersonating, // alias
  }), [
    user, baseUser, impersonatedUser,
    token, loading, error,
    isImpersonating, devImpersonateAs, stopImpersonating, login, logout, refreshMe
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
